import { AccessRole } from '../models/user';

// item 323 — role impersonation (super-admin testing tool, stays in prod).
// State is PER-TAB (sessionStorage): a refresh keeps it, other tabs are unaffected,
// closing the tab ends it. The backend re-verifies the caller is a super-admin on
// every request — this client state grants nothing by itself.

export const IMPERSONATE_HEADER = 'X-Basix-Impersonate-Role';
// item 843 — previewing the external CustomerBuilder role also names the customer to preview as
export const IMPERSONATE_CUSTOMER_HEADER = 'X-Basix-Impersonate-Customer';
// item 851 — impersonate a specific USER (super-admin): the whole app runs as them.
export const IMPERSONATE_USER_HEADER = 'X-Basix-Impersonate-User';
const KEY = 'basix.impersonateRole';
const CUSTOMER_KEY = 'basix.impersonateCustomer';
const USER_KEY = 'basix.impersonateUser';
const USER_NAME_KEY = 'basix.impersonateUserName';

// enum NAME strings — the backend parses these with Enum.TryParse<AccessRole>
export const IMPERSONATABLE_ROLES: { value: string; label: string }[] = [
  { value: 'Admin', label: 'Admin' },
  { value: 'Accountant', label: 'Accountant' },
  { value: 'Team', label: 'Team' },
  { value: 'OutsideSales', label: 'Outside Sales' },
  { value: 'Executive', label: 'Executive' }, // item 594
];

export const getImpersonatedRoleName = (): string | null => {
  try {
    return window.sessionStorage.getItem(KEY);
  } catch {
    return null;
  }
};

// the frontend AccessRole enum value for the impersonated role (drives useCurrentUser)
export const getImpersonatedRole = (): AccessRole | undefined => {
  const name = getImpersonatedRoleName();
  if (!name) return undefined;
  const map: Record<string, AccessRole> = {
    Admin: AccessRole.Admin,
    Accountant: AccessRole.Accountant,
    Team: AccessRole.Team,
    OutsideSales: AccessRole.OutsideSales,
    Executive: AccessRole.Executive, // item 594
    CustomerBuilder: AccessRole.CustomerBuilder, // item 843 — super-admin preview
  };
  return map[name];
};

// item 843 — the customer a CustomerBuilder preview is bound to (null when not previewing one)
export const getImpersonatedCustomerId = (): string | null => {
  try {
    return window.sessionStorage.getItem(CUSTOMER_KEY);
  } catch {
    return null;
  }
};

// item 851 — the user being impersonated (super-admin), + their display name for the banner.
export const getImpersonatedUserId = (): string | null => {
  try {
    return window.sessionStorage.getItem(USER_KEY);
  } catch {
    return null;
  }
};

export const getImpersonatedUserName = (): string | null => {
  try {
    return window.sessionStorage.getItem(USER_NAME_KEY);
  } catch {
    return null;
  }
};

export const startUserImpersonation = (userId: string, name: string): void => {
  try {
    // user-impersonation is standalone — clear any role/customer preview
    window.sessionStorage.removeItem(KEY);
    window.sessionStorage.removeItem(CUSTOMER_KEY);
    window.sessionStorage.setItem(USER_KEY, userId);
    window.sessionStorage.setItem(USER_NAME_KEY, name || '');
  } catch { /* storage unavailable — no-op */ }
  window.location.assign('/');
};

// set/clear reload the app: react-query caches + mounted routes all assume one role,
// so a clean slate is the only honest way to switch identity
export const startImpersonation = (roleName: string, customerId?: string): void => {
  try {
    window.sessionStorage.setItem(KEY, roleName);
    // item 843 — CustomerBuilder preview carries a customer; other roles clear it
    if (customerId) window.sessionStorage.setItem(CUSTOMER_KEY, customerId);
    else window.sessionStorage.removeItem(CUSTOMER_KEY);
  } catch { /* storage unavailable — no-op */ }
  window.location.assign('/');
};

export const stopImpersonation = (): void => {
  try {
    window.sessionStorage.removeItem(KEY);
    window.sessionStorage.removeItem(CUSTOMER_KEY);
    window.sessionStorage.removeItem(USER_KEY);
    window.sessionStorage.removeItem(USER_NAME_KEY);
  } catch { /* storage unavailable — no-op */ }
  window.location.assign('/');
};
